import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
// @ts-check
import { useCallback, useEffect, useRef, useState } from 'react';
import t from '@instructure/quiz-i18n/format-message';

/**
 * Custom hook to load and render a remote component asynchronously.
 *
 * @param {() => Promise<{
 *   render: (container: HTMLElement, props: unknown) => void,
 *   unmount?: () => void
 * }>} loadRemoteModule - A function that returns a promise resolving to the remote component module
 * @param {unknown | undefined} props - Props to pass to the remote component when rendering
 * @param {boolean} enabled - Whether the remote component should be loaded and rendered
 *
 * @returns {{
 *   containerRef: React.MutableRefObject<HTMLDivElement | null>,
 *   componentActivityState: 'idle' | 'pending' | 'success' | 'error',
 *   componentError: Error | null,
 *   unmount: () => void
 * }} An object containing the container ref and unmount function
 */
export function useRemoteComponent(loadRemoteModule, props) {
  var enabled = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  var mountCancelled = useRef(false);
  var _useState = useState(/** @type {'idle' | 'pending' | 'success' | 'error'} */'idle'),
    _useState2 = _slicedToArray(_useState, 2),
    componentActivityState = _useState2[0],
    setComponentActivityState = _useState2[1];
  var _useState3 = useState(/** @type {Error | null} */null),
    _useState4 = _slicedToArray(_useState3, 2),
    componentError = _useState4[0],
    setComponentError = _useState4[1];

  /**
   * @type {React.MutableRefObject<{
   *   render: (container: HTMLElement, props: unknown) => void,
   *   unmount?: () => void
   * } | null>}
   */
  var remoteModuleRef = useRef(null);

  /**
   * @type {React.MutableRefObject<HTMLDivElement | null>}
   */
  var containerRef = useRef(null);

  // Note: This workflow can be converted to a TanStack Query hook in the future.
  useEffect(function () {
    if (loadRemoteModule == null || !enabled || componentActivityState === 'error') {
      return;
    }
    mountCancelled.current = false;
    setComponentActivityState('idle');
    function loadModule() {
      return _loadModule.apply(this, arguments);
    }
    function _loadModule() {
      _loadModule = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var module;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;
              if (!remoteModuleRef.current) {
                _context.next = 4;
                break;
              }
              setComponentActivityState('success');
              return _context.abrupt("return", remoteModuleRef.current);
            case 4:
              setComponentActivityState('pending');
              _context.next = 7;
              return loadRemoteModule();
            case 7:
              module = _context.sent;
              setComponentActivityState('success');
              return _context.abrupt("return", module);
            case 12:
              _context.prev = 12;
              _context.t0 = _context["catch"](0);
              setComponentActivityState('error');
              setComponentError(new Error(t('Failed to load remote module: {error}', {
                error: _context.t0
              })));
            case 16:
            case "end":
              return _context.stop();
          }
        }, _callee, null, [[0, 12]]);
      }));
      return _loadModule.apply(this, arguments);
    }
    loadModule().then(function (module) {
      if (module) {
        remoteModuleRef.current = module;
      }
      var container = containerRef.current;
      if (container == null || module == null || mountCancelled.current) {
        return;
      }
      module.render(container, props);
    });
    return function () {
      mountCancelled.current = true;
    };
  }, [loadRemoteModule, props, enabled]);
  var unmount = useCallback(function () {
    var _module$unmount;
    if (mountCancelled.current || !containerRef.current) {
      return;
    }
    var module = remoteModuleRef.current;
    if (!module) {
      return;
    }
    (_module$unmount = module.unmount) === null || _module$unmount === void 0 || _module$unmount.call(module);
  }, []);
  return {
    containerRef: containerRef,
    componentActivityState: componentActivityState,
    componentError: componentError,
    unmount: unmount
  };
}